import type { ReactNode } from 'react';
import { C, SERIF } from '../theme';
import { Icon } from '../icons';

/** A quiet inline spinner (CSS keyframes injected once by index.css). */
export function Spinner({ size = 20, color = C.accent }: { size?: number; color?: string }) {
  return (
    <span
      className="premise-spin"
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        border: `2.5px solid ${C.line}`,
        borderTopColor: color,
        display: 'inline-block',
      }}
    />
  );
}

/** Centered loading block for a panel/screen. */
export function Loading({ label = 'Loading…', minHeight = 200 }: { label?: string; minHeight?: number }) {
  return (
    <div
      style={{
        minHeight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        color: C.muted,
      }}
    >
      <Spinner />
      <div style={{ fontSize: 13 }}>{label}</div>
    </div>
  );
}

/** Empty / not-yet-wired state. Used when an endpoint returns nothing or is down. */
export function Empty({
  icon = 'doc',
  title,
  hint,
  minHeight = 200,
  children,
}: {
  icon?: string;
  title: string;
  hint?: string;
  minHeight?: number;
  children?: ReactNode;
}) {
  return (
    <div
      style={{
        minHeight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        color: C.muted,
        textAlign: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: 54,
          height: 54,
          borderRadius: 14,
          background: C.soft,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: C.accent2,
        }}
      >
        <Icon n={icon} size={24} />
      </div>
      <div style={{ fontFamily: SERIF, fontSize: 20, color: C.ink }}>{title}</div>
      {hint && <div style={{ fontSize: 13.5, maxWidth: 340 }}>{hint}</div>}
      {children}
    </div>
  );
}
